import { motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { FaGithub, FaLinkedinIn, FaTwitter } from "./utils/Icons";
import { FiMenu } from "./utils/Icons";
import { MdClose } from "./utils/Icons";
import { Link } from "react-scroll";
import { navLinksData } from "../constants";

const Navbar = () => {
  const [showMenu, setShowMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuTransition = {
    y: { type: "spring", stiffness: 60 },
    opacity: { duration: 0.5 },
    ease: "easeInOut",
  };

  return (
    <header
      className={`w-full h-20 font-titleFont sticky top-0 z-50 px-4 transition-all duration-300 ${scrolled
        ? "navbar-scrolled border-b border-gray-800"
        : "bg-bodyColor border-b border-gray-800"
        }`}
    >
      <div className="relative flex items-center justify-between h-full max-w-screen-xl mx-auto">
        {/* Logo — canonical mark: Shakil. */}
        <div>
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, ...menuTransition }}
            className="text-2xl font-bold tracking-wide uppercase"
          >
            <span className="text-designColor">S</span>
            <span className="text-white">hakil</span>
            <span className="text-designColor ml-0.5">.</span>
          </motion.h2>
        </div>

        {/* Desktop Nav */}
        <nav className="flex items-center gap-4">
          <ul className="items-center hidden gap-6 mdl:inline-flex lg:gap-7">
            {navLinksData.map(({ _id, title, link }, index) => (
              <motion.li
                key={_id}
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 + 0.5, ...menuTransition }}
                className="text-sm font-semibold tracking-wide text-gray-400 duration-300 cursor-pointer hover:text-designColor"
              >
                <Link
                  activeClass="active"
                  to={link}
                  spy={true}
                  smooth={true}
                  offset={-88}
                  duration={500}
                  title={title}
                >
                  {title}
                </Link>
              </motion.li>
            ))}
          </ul>

          {/* Desktop action buttons */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, ...menuTransition }}
            className="items-center hidden gap-3 mdl:flex"
          >
            {/* Primary CTA */}
            <Link
              to="contacts"
              spy={true}
              smooth={true}
              offset={-88}
              duration={500}
              // className="text-xs cursor-pointer btn-primary"
              className="px-4 py-1 btn-secondary cursor-pointer text-sm text-gray-400 font-semibold"
            >
              Contact
            </Link>
          </motion.div>

          {/* Mobile menu button */}
          <button
            onClick={() => setShowMenu((prev) => !prev)}
            type="button"
            aria-label={showMenu ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={showMenu}
            aria-controls="mobile-menu"
            className="inline-flex items-center justify-center w-10 h-10 text-xl bg-black rounded-full cursor-pointer bg-opacity-30 mdl:hidden text-designColor"
          >
            <FiMenu />
          </button>

          {/* Mobile menu — slide-in drawer */}
          <div
            id="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className={`mdl:hidden w-[80%] h-screen overflow-y-auto fixed top-0 left-0 bg-gray-900 p-4 z-[60] ease-in-out duration-500 select-none transition-transform ${!showMenu ? "-translate-x-full" : "translate-x-0 scrollbar-hide"
              }`}
          >
            <div className="flex flex-col gap-10">
              {/* Branding in drawer */}
              <div className="flex flex-col w-full h-full gap-6">
                <div>
                  <h2 className="text-2xl font-bold uppercase font-titleFont">
                    <span className="text-designColor">S</span>hakil
                    <span className="text-designColor ml-0.5">.</span>
                  </h2>
                  <p className="mt-4 text-sm leading-6 text-gray-400">
                    Senior Full-Stack Engineer specializing in multi-tenant SaaS,
                    enterprise systems, and AI workflow automation.
                  </p>
                </div>
              </div>

              {/* Nav links */}
              <div>
                <ul className="flex flex-col gap-4">
                  {navLinksData.map(({ _id, title, link }) => (
                    <li
                      key={_id}
                      className="text-base font-semibold tracking-wide text-gray-400 duration-300 cursor-pointer hover:text-designColor"
                    >
                      <Link
                        onClick={() => setShowMenu(false)}
                        activeClass="active"
                        to={link}
                        spy={true}
                        smooth={true}
                        offset={-88}
                        duration={500}
                        title={title}
                      >
                        {title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Mobile LinkedIn + Get In Touch */}
              <div className="flex flex-col gap-3">
                <a
                  href="https://www.linkedin.com/in/shakilahmmed1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="justify-center text-center btn-outline"
                  onClick={() => setShowMenu(false)}
                >
                  View LinkedIn Profile
                </a>
                <Link
                  to="contacts"
                  spy={true}
                  smooth={true}
                  offset={-88}
                  duration={500}
                  className="justify-center text-center cursor-pointer btn-primary"
                  onClick={() => setShowMenu(false)}
                >
                  Get In Touch
                </Link>
              </div>

              {/* Socials */}
              <div>
                <h2 className="mb-4 text-xs font-semibold tracking-widest text-gray-400 uppercase">
                  Connect
                </h2>
                <div className="flex gap-4">
                  <a
                    href="https://github.com/grzafnan"
                    target="_blank"
                    aria-label="GitHub profile"
                    rel="noopener noreferrer"
                    className="bannerIcon"
                  >
                    <FaGithub />
                  </a>
                  <a
                    href="https://www.linkedin.com/in/shakilahmmed1"
                    target="_blank"
                    aria-label="LinkedIn profile"
                    rel="noopener noreferrer"
                    className="bannerIcon"
                  >
                    <FaLinkedinIn />
                  </a>
                  <a
                    href="https://twitter.com/shakil_ahmmed7"
                    target="_blank"
                    aria-label="Twitter / X profile"
                    rel="noopener noreferrer"
                    className="bannerIcon"
                  >
                    <FaTwitter />
                  </a>
                </div>
              </div>
            </div>

            {/* Close button */}
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setShowMenu(false)}
              className="absolute top-4 right-4 p-1.5 bg-black bg-opacity-50 inline-flex items-center justify-center rounded-full text-gray-400 hover:text-red-400 duration-300 text-xl cursor-pointer"
            >
              <MdClose />
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
